// @ts-check

/**
 * ACE mega menu — reliable open-on-hover engine.
 *
 * Panels self-render (each `ace-mega-menu-panel` block reads its own settings) and
 * are keyed to their top-level trigger via `data-menu-key`. This element pairs each
 * panel with its trigger and drives open/close on hover + focus.
 *
 * Single source of truth: JS toggles `data-open` on the matched PANEL; CSS reveals
 * it only via `[data-open]` — never `:hover`. Nothing is measured, so lazy hydration
 * can't make a panel open empty. A short close delay bridges the visual gap between
 * a trigger and its (full-width, header-anchored) panel.
 */
class AceMegaMenu extends HTMLElement {
  /** ms to wait before opening on hover (hover intent) */
  #openDelay = 70;
  /** ms to wait before closing after the pointer leaves */
  #closeDelay = 180;

  /** @type {string | null} */
  #openKey = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;
  /** @type {Map<string, { item: HTMLElement, trigger: HTMLElement, panel: HTMLElement }>} */
  #pairs = new Map();
  /** @type {MutationObserver | null} */
  #observer = null;
  /** Removes the current generation of listeners when we re-wire. @type {AbortController | null} */
  #controller = null;
  /** Coalesces bursts of mutations into a single re-wire. */
  #setupQueued = false;

  connectedCallback() {
    this.#setup();
    // The panels are server-rendered children, but the theme can upgrade this
    // element before they exist AND re-render / morph the header subtree after
    // (lazy hydration, sticky header, section reload) — either drops our wiring.
    // Keep watching and re-wire whenever the children change.
    this.#observer = new MutationObserver(() => this.#queueSetup());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#clearOpenTimer();
    this.#clearCloseTimer();
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  /** Debounce re-wiring so a burst of morph mutations triggers one #setup. */
  #queueSetup() {
    if (this.#setupQueued) return;
    this.#setupQueued = true;
    queueMicrotask(() => {
      this.#setupQueued = false;
      this.#setup();
    });
  }

  /**
   * Pair each panel with its trigger. Idempotent and re-runnable: a fresh
   * AbortController drops the previous generation of listeners so repeated runs
   * never stack duplicates. A no-op while the panels are not in the DOM yet.
   */
  #setup() {
    const panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-ace-menu-panel]')));
    if (panels.length === 0) return;

    // Our own DOM writes below (panel.remove) would re-trigger the observer —
    // pause it for the duration of the wiring, then resume for future morphs.
    this.#observer?.disconnect();

    // Drop the previous generation of listeners and reset transient state.
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#pairs.clear();
    this.#openKey = null;

    // Configured promo panels (self-rendered `ace-mega-menu-panel` blocks) take
    // precedence over the host's auto subcategory panels: collect every key that
    // has a configured panel so we can discard the matching auto panel below.
    const configuredKeys = new Set(
      panels
        .filter((panel) => !panel.hasAttribute('data-ace-menu-auto'))
        .map((panel) => panel.getAttribute('data-menu-key'))
        .filter(Boolean)
    );

    for (const panel of panels) {
      const key = panel.getAttribute('data-menu-key');
      if (!key) continue;

      // Drop the auto panel when a configured panel already owns this key.
      if (panel.hasAttribute('data-ace-menu-auto') && configuredKeys.has(key)) {
        panel.remove();
        continue;
      }
      const item = /** @type {HTMLElement | null} */ (
        this.querySelector(`.ace-mega-menu__item[data-menu-key="${CSS.escape(key)}"]`)
      );
      const trigger = /** @type {HTMLElement | null} */ (item?.querySelector('.ace-mega-menu__toplink') ?? null);
      if (!item || !trigger) continue;

      this.#pairs.set(key, { item, trigger, panel });

      // Mark the item as interactive and expose a11y state on its trigger.
      item.dataset.aceMenuItem = '';
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');

      item.addEventListener('pointerenter', () => this.#scheduleOpen(key), { signal });
      item.addEventListener('pointerleave', () => this.#scheduleClose(key), { signal });
      item.addEventListener('focusin', () => this.#open(key), { signal });
      item.addEventListener('focusout', (event) => {
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (next && item.contains(next)) return;
        this.#scheduleClose(key);
      }, { signal });

      panel.addEventListener('pointerenter', () => {
        this.#clearCloseTimer();
        this.#clearOpenTimer();
      }, { signal });
      panel.addEventListener('pointerleave', () => this.#scheduleClose(key), { signal });
      panel.addEventListener('focusout', (event) => {
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (next && panel.contains(next)) return;
        this.#scheduleClose(key);
      }, { signal });

      this.#wireHighlights(panel, signal);
    }

    this.addEventListener('keydown', this.#onKeyDown, { signal });

    this.#observer?.observe(this, { childList: true, subtree: true });
  }

  /**
   * Featured-product highlights: the first highlight's product image shows
   * immediately; hovering (or focusing) another highlight swaps the panel's
   * promo image to that product, and leaving the list returns to the first.
   * The image URL travels on the link (`data-ace-promo-image`) because nested
   * blocks self-render and the panel can't read their settings.
   * @param {HTMLElement} panel
   * @param {AbortSignal} signal
   */
  #wireHighlights(panel, signal) {
    const image = /** @type {HTMLImageElement | null} */ (panel.querySelector('.ace-mega-menu__promo-product'));
    if (!image) return;
    const links = /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('[data-ace-promo-image]')));
    if (links.length === 0) return;

    /** @param {HTMLElement} link */
    const swap = (link) => {
      const src = link.getAttribute('data-ace-promo-image');
      if (!src) return;
      image.src = src;
      image.hidden = false;
    };

    const list = /** @type {HTMLElement} */ (links[0].closest('.ace-mega-menu__promo-highlights') ?? panel);
    for (const link of links) {
      link.addEventListener('pointerenter', () => swap(link), { signal });
      link.addEventListener('focus', () => swap(link), { signal });
    }
    list.addEventListener('pointerleave', () => swap(links[0]), { signal });
    list.addEventListener('focusout', (event) => {
      const next = /** @type {Node | null} */ (event.relatedTarget);
      if (next && list.contains(next)) return;
      swap(links[0]);
    }, { signal });

    // Default state: the first highlight's product image.
    swap(links[0]);
  }

  /** @param {string} key */
  #scheduleOpen(key) {
    this.#clearCloseTimer();
    this.#clearOpenTimer();
    this.#openTimer = setTimeout(() => this.#open(key), this.#openDelay);
  }

  /** @param {string} key */
  #open(key) {
    this.#clearOpenTimer();
    if (this.#openKey && this.#openKey !== key) this.#closeNow(this.#openKey);
    const refs = this.#pairs.get(key);
    if (!refs) return;
    refs.panel.setAttribute('data-open', '');
    refs.trigger.classList.add('is-open');
    refs.trigger.setAttribute('aria-expanded', 'true');
    this.#openKey = key;
  }

  /** @param {string} key */
  #scheduleClose(key) {
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => this.#closeNow(key), this.#closeDelay);
  }

  /** @param {string} key */
  #closeNow(key) {
    const refs = this.#pairs.get(key);
    if (!refs) return;
    refs.panel.removeAttribute('data-open');
    refs.trigger.classList.remove('is-open');
    refs.trigger.setAttribute('aria-expanded', 'false');
    if (this.#openKey === key) this.#openKey = null;
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#openKey) return;
    const refs = this.#pairs.get(this.#openKey);
    this.#closeNow(this.#openKey);
    refs?.trigger.focus();
  };

  #clearOpenTimer() {
    if (this.#openTimer) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
  }

  #clearCloseTimer() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }
}

if (!customElements.get('ace-mega-menu')) {
  customElements.define('ace-mega-menu', AceMegaMenu);
}
